import React from 'react';
import { format } from 'date-fns';
import { CalendarClock, Sparkles } from 'lucide-react';

/**
 * One-tap suggestion strip shown when a client picks a time that isn't
 * available. Keeps the rebook/reschedule flow from dead-ending, each
 * suggestion sets both date and time in a single tap.
 *
 * Suggestions should already be computed by the caller with
 * getNextAvailableSlots so this component stays dumb and re-usable.
 */
export function NextAvailableSlots({ suggestions, onPick, loading = false, testIdPrefix = 'next-slot' }) {
  if (loading) {
    return (
      <div
        className="mt-3 p-3 rounded-lg bg-[#F9F6F0] border border-[#E6E4DD] text-sm text-stone-500"
        data-testid={`${testIdPrefix}-loading`}
      >
        Finding the nearest openings...
      </div>
    );
  }

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div
      className="mt-3 p-3 rounded-lg bg-[#FFF8E7] border border-[#D4AF37]/40"
      data-testid={`${testIdPrefix}-wrapper`}
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" />
        <p className="text-xs font-medium text-[#1A1A1A]">Nearest openings, one tap to pick</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((s, idx) => {
          const dateObj = s.date instanceof Date ? s.date : new Date(s.date);
          const label = s.sameDay
            ? `Today at ${s.time}`
            : `${format(dateObj, 'EEE d MMM')} at ${s.time}`;
          return (
            <button
              key={`${format(dateObj, 'yyyy-MM-dd')}-${s.time}-${idx}`}
              type="button"
              onClick={() => onPick(s)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-[#D4AF37] text-[#1A1A1A] hover:bg-[#D4AF37] hover:text-white transition-colors"
              data-testid={`${testIdPrefix}-pill-${idx}`}
            >
              <CalendarClock className="h-3.5 w-3.5" />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
